// Warn before overwriting — picking an existing project can wipe its saved session.

import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Swal from "sweetalert2";
import codebaseService from "../../services/codebaseService";

export default function ProjectRequiredPopup({ open, onClose, onProjectReady }) {
  const [mode, setMode] = useState("create");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [framework, setFramework] = useState("tensorflow");
  const [isSupervised, setIsSupervised] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [existingCodebases, setExistingCodebases] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const userId = sessionStorage.getItem("userId");
  const sessionToken = sessionStorage.getItem("session_token");

  useEffect(() => {
    if (!open) return;

    setMode("create");
    setTitle("");
    setDescription("");
    setSelectedId("");

    setLoadingExisting(true);
    codebaseService
      .getCodebasesForUser(userId, sessionToken)
      .then((data) => setExistingCodebases(Array.isArray(data) ? data : []))
      .catch(() => setExistingCodebases([]))
      .finally(() => setLoadingExisting(false));
  }, [open, userId, sessionToken]);

  if (!open) return null;

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) {
      Swal.fire("Error!", "Both title and description are required.", "error");
      return;
    }

    setSubmitting(true);
    try {
      const modelType = isSupervised ? "supervised" : "unsupervised";
      const response = await codebaseService.addCodebase(
        userId,
        sessionToken,
        title,
        description,
        modelType,
        framework
      );

      if (!response.codebaseId) {
        throw new Error("No codebaseId returned from backend");
      }

      onProjectReady(response.codebaseId);
    } catch (error) {
      console.error("Error creating codebase:", error);
      Swal.fire(
        "Error!",
        "Could not create the project. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelect = async () => {
    if (!selectedId) {
      Swal.fire("Select a Project", "Please choose a project first.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const existingSession = await codebaseService.getSession(selectedId);

      if (existingSession?.session_data) {
        const result = await Swal.fire({
          icon: "warning",
          title: "This Project Already Has Saved Work",
          text:
            "Continuing will overwrite its previously saved code, chat, and results. " +
            "This cannot be undone.",
          showCancelButton: true,
          confirmButtonText: "Overwrite",
          confirmButtonColor: "#d33",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
          setSubmitting(false);
          return;
        }
      }

      onProjectReady(selectedId);
    } catch (error) {
      console.error("Error checking existing session:", error);
      Swal.fire("Error!", "Could not load that project. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="p-6 border-b flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-800">Save to a Project</h3>
            <p className="text-sm text-slate-500 mt-1">
              Your work isn't attached to a project yet. Create a new one or
              attach it to a project you already have.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 flex-shrink-0 ml-4"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setMode("create")}
              className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition ${
                mode === "create"
                  ? "bg-sky-500 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Create New Project
            </button>
            <button
              type="button"
              onClick={() => setMode("select")}
              className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition ${
                mode === "select"
                  ? "bg-sky-500 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Select Existing Project
            </button>
          </div>

          {mode === "create" ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                  Description
                </label>
                <textarea
                  rows="3"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent shadow-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                  Learning Type
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsSupervised(true)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                      isSupervised
                        ? "bg-sky-100 text-sky-700 border border-sky-300"
                        : "bg-slate-100 text-slate-600 border border-transparent hover:bg-slate-200"
                    }`}
                  >
                    Supervised
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsSupervised(false)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                      !isSupervised
                        ? "bg-sky-100 text-sky-700 border border-sky-300"
                        : "bg-slate-100 text-slate-600 border border-transparent hover:bg-slate-200"
                    }`}
                  >
                    Unsupervised
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                  Framework
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFramework("tensorflow")}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                      framework === "tensorflow"
                        ? "bg-orange-500 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    TensorFlow
                  </button>
                  <button
                    type="button"
                    onClick={() => setFramework("pytorch")}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                      framework === "pytorch"
                        ? "bg-red-500 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    PyTorch
                  </button>
                </div>
              </div>
            </div>
          ) : loadingExisting ? (
            <p className="text-sm text-slate-500 py-4">Loading your projects...</p>
          ) : existingCodebases.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">
              You don't have any existing projects yet — create a new one instead.
            </p>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Project
              </label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full p-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent shadow-sm bg-white"
              >
                <option value="">Choose a project...</option>
                {existingCodebases.map((cb) => (
                  <option key={cb.codebaseId} value={cb.codebaseId}>
                    {cb.codebase_name || `Codebase #${cb.codebaseId}`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="p-6 border-t flex gap-3">
          <button
            onClick={mode === "create" ? handleCreate : handleSelect}
            disabled={
              submitting || (mode === "select" && existingCodebases.length === 0)
            }
            className="flex-1 bg-sky-500 hover:bg-sky-600 text-white font-semibold px-4 py-2.5 rounded-lg shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {submitting
              ? "Saving..."
              : mode === "create"
                ? "Create & Save"
                : "Attach & Save"}
          </button>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
